import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
  TooltipFormatter,
} from "recharts";
import { makeStyles } from "@material-ui/core/styles";

import { IWithballData } from "types";
import {
  calculatePecentage,
  groupWithBallByPosition,
  groupWithBallByFromPlayer,
} from "utils";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";

interface IDefensePanelProps {
  tackleDataArray: IWithballData[];
  interceptionDataArray: IWithballData[];
  clearanceDataArray: IWithballData[];
}

const ASPECT = 2;
const CHART_WIDTH = "100%";
const BAR_SIZE = 20;
const MARGIN = { top: 5, right: 5, left: 5, bottom: 5 };

const useStyles = makeStyles(() => ({
  bottomAlignGrid: {
    position: "relative",
  },
  bottomAlignContent: {
    position: "absolute",
    bottom: 0,
    height: "auto !important",
  },
}));

export const DefensePanel = ({
  tackleDataArray,
  interceptionDataArray,
  clearanceDataArray,
}: IDefensePanelProps) => {
  const classes = useStyles();

  const {
    tackleSummary,
    interceptionSummary,
    tackleMatrix,
    interceptionMatrix,
    clearMatrix,
  } = useMemo(() => {
    return {
      tackleSummary: groupWithBallByPosition(tackleDataArray),
      interceptionSummary: groupWithBallByPosition(interceptionDataArray),
      tackleMatrix: groupWithBallByFromPlayer(tackleDataArray),
      interceptionMatrix: groupWithBallByFromPlayer(interceptionDataArray),
      clearMatrix: groupWithBallByFromPlayer(clearanceDataArray),
    };
  }, [tackleDataArray, interceptionDataArray, clearanceDataArray]);

  const tooltipFormatter: TooltipFormatter = (value, name) => {
    const label = intl.get("STATS_LABEL_TACKLE");
    return [value, label];
  };

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    const label = intl.get("STATS_LABEL_TACKLE");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const renderTackleChart = () => {
    if (!tackleMatrix) {
      return null;
    }

    const data = Object.getOwnPropertyNames(tackleMatrix).map((property) => ({
      name: tackleMatrix[property][0].fromName,
      total: tackleMatrix[property].length,
    }));

    if (data.length <= 0) {
      return null;
    }

    return (
      <ResponsiveContainer
        width={CHART_WIDTH}
        aspect={ASPECT}
        className={classes.bottomAlignContent}
      >
        <BarChart data={data} margin={MARGIN}>
          <XAxis dataKey="name" angle={-45} interval={0} />
          <YAxis />
          <Tooltip formatter={tooltipFormatter} />
          <Legend formatter={legendFormatter} />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderInterceptionChart = () => {
    if (!interceptionMatrix) {
      return null;
    }

    const data = Object.getOwnPropertyNames(interceptionMatrix).map(
      (property) => ({
        name: interceptionMatrix[property][0].fromName,
        total: interceptionMatrix[property].length,
      })
    );

    if (data.length <= 0) {
      return null;
    }

    return (
      <ResponsiveContainer
        width={CHART_WIDTH}
        aspect={ASPECT}
        className={classes.bottomAlignContent}
      >
        <BarChart data={data} margin={MARGIN}>
          <XAxis dataKey="name" angle={-45} interval={0} />
          <YAxis />
          <Tooltip formatter={tooltipFormatter} />
          <Legend formatter={legendFormatter} />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderClearChart = () => {
    if (!clearMatrix) {
      return null;
    }

    const data = Object.getOwnPropertyNames(clearMatrix).map((property) => ({
      name: clearMatrix[property][0].fromName,
      total: clearMatrix[property].length,
    }));

    if (data.length <= 0) {
      return null;
    }

    return (
      <ResponsiveContainer
        width={CHART_WIDTH}
        aspect={ASPECT}
        className={classes.bottomAlignContent}
      >
        <BarChart data={data} margin={MARGIN}>
          <XAxis dataKey="name" angle={-45} interval={0} />
          <YAxis />
          <Tooltip formatter={tooltipFormatter} />
          <Legend formatter={legendFormatter} />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_LABEL_TACKLE")}: ${
          tackleDataArray.length
        }`}</Typography>
        <Box mt={1} />
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          tackleSummary.selfHalf.length
        } (${calculatePecentage(
          tackleSummary.selfHalf.length,
          tackleDataArray.length
        )})`}</Typography>
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          tackleSummary.otherHalf.length
        } (${calculatePecentage(
          tackleSummary.otherHalf.length,
          tackleDataArray.length
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={tackleDataArray} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderTackleChart()}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_LABEL_INTERCEPTION")}: ${
          interceptionDataArray.length
        }`}</Typography>
        <Box mt={1} />
        <Typography>{`${intl.get("STATS_POS_SELF")}: ${
          interceptionSummary.selfHalf.length
        } (${calculatePecentage(
          interceptionSummary.selfHalf.length,
          interceptionDataArray.length
        )})`}</Typography>
        <Typography>{`${intl.get("STATS_POS_OTHER")}: ${
          interceptionSummary.otherHalf.length
        } (${calculatePecentage(
          interceptionSummary.otherHalf.length,
          interceptionDataArray.length
        )})`}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={interceptionDataArray} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderInterceptionChart()}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4">{`${intl.get("STATS_LABEL_CLEAR")}: ${
          clearanceDataArray.length
        }`}</Typography>
        <Box mt={1} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatisticsBoard data={clearanceDataArray} />
          </Grid>
          <Grid item xs={6} className={classes.bottomAlignGrid}>
            {renderClearChart()}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
